import { SceneEngine } from "@animech/axiom-scene-utilities";
import {
	Color,
	ELEMENTTYPE_TEXT,
	Entity,
	LAYERID_WORLD,
	Quat,
	StandardMaterial,
	Vec2,
	Vec3,
	Vec4,
} from "playcanvas";

const arrowEntityName = "arrow-entity";

export class AreaDescriptionManager {
	private _sceneEngine: SceneEngine;
	private _fontId: number | undefined;

	public constructor(sceneEngine: SceneEngine) {
		this._sceneEngine = sceneEngine;
	}

	public render(width: number, depth: number) {
		this._deleteMeasurementArrows();
		this._createMeasurementArrows(this._sceneEngine.Application, width, depth);
	}

	private _deleteMeasurementArrows() {
		while (this._sceneEngine.Application.root.findByName(arrowEntityName)) {
			this._sceneEngine.Application.root.findByName(arrowEntityName)?.destroy();
		}
	}

	private _createMeasurementArrows(
		app: pc.Application,
		width: number,
		depth: number,
	) {
		this._createMeasurementArrow(
			app,
			new Vec3(width / 2, 0, depth / 2),
			new Vec3(-width / 2, 0, depth / 2),
			width.toFixed(2) + " m",
		);
		this._createMeasurementArrow(
			app,
			new Vec3(-width / 2, 0, -depth / 2),
			new Vec3(-width / 2, 0, depth / 2),
			depth.toFixed(2) + " m",
			true,
		);
	}

	private _createMeasurementArrow(
		app: pc.Application,
		start: pc.Vec3,
		end: pc.Vec3,
		label: string,
		rotateLabel = false,
	) {
		const color = new Color(0.4, 0.4, 0.4);
		const headSize = 0.025;
		const arrowThickness = 0.005;
		const group = new Entity(arrowEntityName);

		const line = new Entity();
		line.addComponent("render", {
			type: "cylinder",
		});
		line.setLocalScale(arrowThickness, start.distance(end), arrowThickness);

		const lineCenter = new Vec3().add2(start, end).mulScalar(0.5);
		line.setPosition(lineCenter);
		line.lookAt(end);
		line.rotateLocal(90, 0, 0);

		const mat = new StandardMaterial();
		mat.diffuse = color;
		mat.useLighting = false;
		mat.update();
		if (line.render) {
			line.render.material = mat;
		}
		group.addChild(line);

		const arrowHead = new Entity();
		arrowHead.addComponent("render", { type: "cone" });
		arrowHead.setLocalScale(headSize, headSize, headSize);
		arrowHead.setPosition(start);
		arrowHead.lookAt(end);
		arrowHead.rotateLocal(90, 0, 0);
		if (arrowHead.render) {
			arrowHead.render.material = mat;
		}
		group.addChild(arrowHead);

		const screen = new Entity("screen");
		screen.addComponent("screen", {
			screenSpace: false,
			layers: [LAYERID_WORLD],
		});

		const text = new Entity();
		const textOffsetFromArrow = 0.1;
		const textLocation = new Vec3().add2(
			lineCenter,
			new Vec3(
				rotateLabel ? -textOffsetFromArrow : 0,
				0,
				rotateLabel ? 0 : textOffsetFromArrow,
			),
		);
		text.setLocalPosition(textLocation);
		text.setLocalRotation(
			new Quat().setFromEulerAngles(-90, rotateLabel ? -90 : 0, 0),
		);
		if (this._fontId === undefined) {
			const fontAssets = app.assets.filter((a) => a.type === "font");
			if (fontAssets.length > 0) {
				this._fontId = fontAssets[0].id;
			}
		}

		text.addComponent("element", {
			pivot: new Vec2(0.5, 0.5),
			anchor: new Vec4(0.5, 0.5, 0.5, 0.5),
			fontAsset: this._fontId,
			fontSize: 0.16,
			text: label,
			autoWidth: true,
			autoHeight: true,
			type: ELEMENTTYPE_TEXT,
			color,
			layers: [LAYERID_WORLD],
		});

		// Force depth testing on the element material (engine-version dependent)
		const applyDepthTest = () => {
			const el = text.element;
			const mat = el?.material;
			if (mat) {
				mat.depthTest = true;
				mat.depthWrite = false;
				mat.update?.();
			}
		};

		screen.addChild(text);
		group.addChild(screen);
		app.root.addChild(group);

		text.element?.on?.("set:material", applyDepthTest);
		text.element?.on?.("set:fontAsset", applyDepthTest);
	}
}
